import { loadBaseCSS } from './button-utils.js';
import { attachInternalsSafe, generateControlId, getFormControlStyles, isAttrTruthy, parseSize, parseValidateOn, reflectBoolean, upgradeProperty } from './form-control.js';

export interface FieldValidity {
  valid: boolean;
  valueMissing: boolean;
  tooShort: boolean;
  tooLong: boolean;
  customError: boolean;
  badInput?: boolean;
}

/** Common native-form lifecycle for the new textarea and switch primitives. */
export abstract class QdsNativeField extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;
  static get observedAttributes(): string[] {
    return ['name', 'label', 'aria-label', 'aria-labelledby', 'hint', 'error-text', 'disabled', 'readonly', 'required', 'invalid', 'size', 'validate-on'];
  }
  protected _internals?: ElementInternals;
  protected _id = generateControlId('field');
  protected _control?: HTMLInputElement | HTMLTextAreaElement;
  protected _dirty = false;
  protected _showError = false;
  private _fieldsetDisabled = false;
  private _customError = '';
  private _validity: FieldValidity = { valid: true, valueMissing: false, tooShort: false, tooLong: false, customError: false };
  private _message = '';
  private _onInvalid = (): void => { this._showError = true; this._sync(); };
  protected readonly _styles = `${getFormControlStyles()}
    :host { font-family: var(--theme-font-family, 'IBM Plex Sans', sans-serif); max-width: 100%; }
    [hidden] { display: none !important; }
    .qds-control-surface:focus { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; box-shadow: none; }
    .qds-hint, .qds-counter { color: var(--theme-text-secondary, #555); }
    .qds-feedback { display: grid; min-block-size: calc(13px * 1.4 + var(--theme-space-1, 4px)); }
    .qds-feedback > * { grid-area: 1 / 1; }
    /* Both messages retain their measured height. Revealing a blur error must
       not move the next action between pointerdown and pointerup. */
    .qds-feedback > [hidden] { display: block !important; visibility: hidden; }
    @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
    @media (forced-colors: active) { .qds-control-surface { border-color: ButtonText; } }
  `;
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
    const internals = attachInternalsSafe(this);
    if (typeof internals?.setFormValue === 'function' && typeof internals?.setValidity === 'function') this._internals = internals;
  }
  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get hint(): string { return this.getAttribute('hint') || ''; }
  set hint(value: string) { this.setAttribute('hint', value ?? ''); }
  get errorText(): string { return this.getAttribute('error-text') || ''; }
  set errorText(value: string) { this.setAttribute('error-text', value ?? ''); }
  get disabled(): boolean { return this._fieldsetDisabled || isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', value); }
  get readonly(): boolean { return isAttrTruthy(this.getAttribute('readonly')); }
  set readonly(value: boolean) { reflectBoolean(this, 'readonly', value); }
  get required(): boolean { return isAttrTruthy(this.getAttribute('required')); }
  set required(value: boolean) { reflectBoolean(this, 'required', value); }
  get invalid(): boolean { return isAttrTruthy(this.getAttribute('invalid')); }
  set invalid(value: boolean) { reflectBoolean(this, 'invalid', value); }
  get size(): 'sm' | 'md' | 'lg' { return parseSize(this.getAttribute('size')); }
  set size(value: string) { this.setAttribute('size', value); }
  get validateOn(): 'input' | 'blur' | 'submit' { return parseValidateOn(this.getAttribute('validate-on')); }
  set validateOn(value: string) { this.setAttribute('validate-on', value); }
  get form(): HTMLFormElement | null { return this._internals?.form || this.closest('form'); }
  get validity(): FieldValidity { return { ...this._validity }; }
  get validationMessage(): string { return this._message; }
  get willValidate(): boolean { return !this.disabled && !this.readonly; }
  setCustomValidity(message: string): void { this._customError = String(message); this._sync(); }
  checkValidity(): boolean {
    this._validate();
    if (this._internals) return this._internals.checkValidity();
    if (!this.validity.valid) this.dispatchEvent(new Event('invalid', { cancelable: true }));
    return this.validity.valid;
  }
  reportValidity(): boolean {
    this._showError = true;
    this._sync();
    if (this._internals) return this._internals.reportValidity();
    const valid = this.checkValidity();
    if (!valid) this.focus();
    return valid;
  }
  override focus(options?: FocusOptions): void { this._control?.focus(options); }
  protected abstract _render(): void;
  protected abstract _syncControl(): void;
  protected abstract _formState(): string;
  protected abstract _formValue(): string | FormData | null;
  protected abstract _constraints(): { flags: Partial<FieldValidity>; message: string };
  protected abstract _reset(): void;
  protected abstract _restore(state: string): void;
  protected abstract _bind(): void;
  protected abstract _unbind(): void;
  protected get _upgradeNames(): string[] { return []; }
  connectedCallback(): void {
    for (const name of ['name', 'label', 'hint', 'errorText', 'disabled', 'readonly', 'required', 'invalid', 'size', 'validateOn', ...this._upgradeNames]) upgradeProperty(this, name);
    if (!this._control) this._render();
    this._bind();
    this.addEventListener('invalid', this._onInvalid);
    this._sync();
  }
  disconnectedCallback(): void { this._unbind(); this.removeEventListener('invalid', this._onInvalid); }
  attributeChangedCallback(): void { this._sync(); }
  formDisabledCallback(disabled: boolean): void { this._fieldsetDisabled = disabled; this._sync(); }
  formResetCallback(): void { this._dirty = false; this._showError = false; this._reset(); this._sync(); }
  formStateRestoreCallback(state: string | File | FormData): void {
    if (typeof state !== 'string') return;
    this._dirty = true;
    this._restore(state);
    this._sync();
  }
  protected _sync(): void {
    if (!this._control) return;
    this._syncControl();
    this._control.disabled = this.disabled;
    this._control.readOnly = this.readonly;
    this._control.required = this.required;
    this._control.setAttribute('aria-required', String(this.required));
    this._control.setAttribute('aria-readonly', String(this.readonly));
    const name = this.getAttribute('aria-label');
    if (name) this._control.setAttribute('aria-label', name); else this._control.removeAttribute('aria-label');
    // Cross-root IDREFs cannot resolve external labels. Copy their text while
    // retaining the native internal label for direct/slotted label consumers.
    const labelledBy = this.getAttribute('aria-labelledby');
    if (!name && labelledBy) {
      const root = this.getRootNode() as Document | ShadowRoot;
      // A detached control can be its own root; only Document/ShadowRoot roots
      // have ID lookup. Keep attribute/property updates safe between mounts.
      const labelsRoot = typeof root.getElementById === 'function' ? root : this.ownerDocument;
      const text = labelledBy.split(/\s+/).map(id => labelsRoot.getElementById(id)?.textContent || '').join(' ').trim();
      if (text) this._control.setAttribute('aria-label', text);
    }
    const label = this.shadowRoot!.querySelector('.label-text');
    if (label) label.textContent = this.label;
    const required = this.shadowRoot!.querySelector('.qds-required-marker') as HTMLElement;
    if (required) required.hidden = !this.required;
    this._internals?.setFormValue(this.disabled ? null : this._formValue(), this._formState());
    this._validate();
    const errorVisible = (this._showError || this.invalid) && !this.validity.valid;
    this._control.setAttribute('aria-invalid', String(errorVisible));
    this.toggleAttribute('data-invalid', errorVisible);
    const hint = this.shadowRoot!.querySelector('.qds-hint') as HTMLElement;
    const error = this.shadowRoot!.querySelector('.qds-error') as HTMLElement;
    hint.textContent = this.hint;
    hint.hidden = !this.hint || errorVisible;
    error.textContent = this._message;
    error.hidden = !errorVisible;
    const described = [!hint.hidden ? hint.id : '', !error.hidden ? error.id : '', this._counterDescription()].filter(Boolean).join(' ');
    if (described) this._control.setAttribute('aria-describedby', described); else this._control.removeAttribute('aria-describedby');
  }
  protected _counterDescription(): string { return ''; }
  protected _onBlur = (): void => { if (this.validateOn === 'blur') this._showError = true; this._sync(); };
  protected _validate(): void {
    const constraints = this.willValidate ? this._constraints() : { flags: {}, message: '' };
    const customError = this.willValidate && Boolean(this._customError || this.invalid);
    const flags = { valueMissing: false, tooShort: false, tooLong: false, ...constraints.flags, customError };
    const valid = !Object.values(flags).some(Boolean);
    const message = valid ? '' : this._customError || this.errorText || constraints.message || 'Please check this field.';
    const changed = JSON.stringify(this._validity) !== JSON.stringify({ valid, ...flags }) || message !== this._message;
    this._validity = { valid, ...flags };
    this._message = message;
    this._internals?.setValidity(valid ? {} : flags, message, this._control);
    if (changed) this.dispatchEvent(new CustomEvent('qds-validity-change', {
      bubbles: true, composed: true, detail: { component: this, valid, validity: this.validity, message },
    }));
  }
  protected _emit(type: 'input' | 'change', detail: object): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail: { component: this, ...detail } }));
  }
}
